import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { fbt } from 'fbtee'
import { toast } from 'sonner'
import { useAuth } from '../../../hooks/useAuth'
import { useGroups } from '../../../hooks/useGroups'
import { useNow } from '../../../hooks/useNow'
import { useUserProfiles } from '../../../hooks/useUserProfiles'
import { useScheduleMarkers } from '../../../hooks/useScheduleMarkers'
import { useScheduleMarkerHides } from '../../../hooks/useScheduleMarkerHides'
import { useBlockedUsers } from '../../../contexts/BlockedUsersContext'
import { useDayCutoff } from '../../../contexts/DayCutoffContext'
import type { ScheduleSurface } from '../../../lib/analytics'
import type { Set as FestivalSet, ScheduleMarker } from '../../../types'
import { ScheduleMarkersContext, type ScheduleMarkersValue } from './scheduleMarkersContext'
import { ScheduleMarkerDialog } from './ScheduleMarkerDialog'
import { computeNowLineMinutes, minutesToTimeInput, timeToMinutes } from './timeUtils'

/**
 * Owns the schedule lines for one event: the viewer's own, the ones friends
 * shared minus what they hid or blocked, and the add/edit dialog. The grid, the
 * FABs and the settings dialog read it through ScheduleMarkersContext.
 */
export function ScheduleMarkersProvider({
  eventId,
  surface,
  currentUserId,
  day,
  daySets,
  timezone,
  children,
}: {
  eventId: string
  surface: ScheduleSurface
  currentUserId: string | null
  day: string | null
  daySets: FestivalSet[]
  timezone?: string
  children: ReactNode
}) {
  const { signedInUid } = useAuth()
  // The prop can name a different user than the session (e.g. a shared view).
  const userId = signedInUid && signedInUid === currentUserId ? signedInUid : null
  const cutoff = useDayCutoff()
  const now = useNow(60_000)
  const markers = useScheduleMarkers(userId, eventId, surface)
  const hides = useScheduleMarkerHides(userId, surface)
  const { isBlocked } = useBlockedUsers()
  const { groups: allGroups } = useGroups(userId)
  const groups = useMemo(
    () => (userId ? allGroups.filter(g => g.memberIds.includes(userId)) : []),
    [allGroups, userId],
  )

  const byTime = useCallback(
    (list: ScheduleMarker[]) =>
      [...list].sort((a, b) => timeToMinutes(a.time, cutoff) - timeToMinutes(b.time, cutoff)),
    [cutoff],
  )
  const { own, shared, addMarker, updateMarker, moveMarker, deleteMarker } = markers
  const { hiddenMarkerIds, hiddenAuthorIds, hideMarker, hideAuthor, showMarker, showAuthor } = hides

  const visibleShared = useMemo(
    () => shared.filter(m => !hiddenMarkerIds.includes(m.id) && !hiddenAuthorIds.includes(m.userId) && !isBlocked(m.userId)),
    [shared, hiddenMarkerIds, hiddenAuthorIds, isBlocked],
  )
  const hiddenLines = useMemo(
    () => byTime(shared.filter(m => hiddenMarkerIds.includes(m.id))),
    [shared, hiddenMarkerIds, byTime],
  )
  const authorIds = useMemo(
    () => [...new Set([...(userId ? [userId] : []), ...shared.map(m => m.userId), ...hiddenAuthorIds])],
    [userId, shared, hiddenAuthorIds],
  )
  const profiles = useUserProfiles(authorIds)

  const [dialog, setDialog] = useState<{ open: boolean; marker?: ScheduleMarker }>({ open: false })

  const value = useMemo<ScheduleMarkersValue>(() => {
    const onDay = (list: ScheduleMarker[]) => byTime(list.filter(m => m.day === day))
    const undoToast = (message: string, undo: () => void) =>
      toast(message, {
        action: { label: fbt('Undo', 'Toast button that shows a schedule line again right after hiding it').toString(), onClick: undo },
      })
    return {
      own: onDay(own),
      shared: onDay(visibleShared),
      hiddenLines,
      hiddenAuthorIds,
      profiles,
      openCreate: () => setDialog({ open: true }),
      openEdit: marker => setDialog({ open: true, marker }),
      move: (marker, minutes) => {
        // Fire and forget: the snapshot shows the move at once, and runMutation toasts a failure.
        moveMarker(marker.id, minutesToTimeInput(minutes)).catch(() => {})
      },
      hideLine: marker => {
        hideMarker(eventId, marker.id).catch(() => {})
        undoToast(
          fbt('Line hidden', 'Toast after hiding one custom schedule line someone shared').toString(),
          () => void showMarker(eventId, marker.id).catch(() => {}),
        )
      },
      hideAuthor: uid => {
        hideAuthor(eventId, uid).catch(() => {})
        const name = profiles[uid]?.displayName ?? ''
        undoToast(
          fbt('Hiding lines from ' + fbt.param('name', name), 'Toast after hiding every schedule line a person shares').toString(),
          () => void showAuthor(eventId, uid).catch(() => {}),
        )
      },
      showLine: marker => void showMarker(eventId, marker.id).catch(() => {}),
      showAuthor: uid => void showAuthor(eventId, uid).catch(() => {}),
    }
  }, [day, own, visibleShared, hiddenLines, hiddenAuthorIds, profiles, byTime, eventId, moveMarker, hideMarker, hideAuthor, showMarker, showAuthor])

  // New lines start at the next quarter hour when the day is today, else at the day's first set.
  const defaultTime = useMemo(() => {
    const mins = daySets.flatMap(s => [timeToMinutes(s.startTime, cutoff), timeToMinutes(s.endTime, cutoff)])
    if (!day || mins.length === 0) return '20:00'
    const first = Math.min(...mins)
    const nowMin = computeNowLineMinutes(now, day, first, Math.max(...mins), true, cutoff, timezone)
    return minutesToTimeInput(nowMin != null ? Math.ceil(nowMin / 15) * 15 : Math.ceil(first / 60) * 60)
  }, [daySets, day, now, cutoff, timezone])

  const editing = dialog.marker
  return (
    <ScheduleMarkersContext.Provider value={value}>
      {children}
      {userId && day && (
        <ScheduleMarkerDialog
          open={dialog.open}
          // Keep the marker through the close animation so the title doesn't flip.
          onOpenChange={open => setDialog(d => ({ ...d, open }))}
          groups={groups}
          marker={editing}
          defaultTime={defaultTime}
          onSave={input => (editing ? updateMarker(editing.id, input) : addMarker(day, input))}
          onDelete={() => (editing ? deleteMarker(editing.id) : Promise.resolve())}
        />
      )}
    </ScheduleMarkersContext.Provider>
  )
}
